import React, { ReactNode, useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Grid,
	Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import BasicPagination from "../../../components/BasicPagination";
import { getSearchModels } from "../../../apis/model";
import { DEFAULT_PAGE_SIZE } from "../../../constants/Constant";
import { Loader } from "../../../components/Loader";
import { ImageViewer } from "../../../components/ImageViewer";

interface TRowData<T> {
	[key: string]: T;
}

export const ModelLists: React.FC = () => {
	const navigate = useNavigate();

	const [models, setModels] = useState<TRowData<ReactNode>[]>([]);
	const [isLoading, setIsLoading] = useState<boolean | false>(true);
	const [page, setPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
	const [count, setCount] = useState<number>(0);

	const fetchModels = async () => {
		try {
			const { data: modelData, counts } = await getSearchModels({
				page: page,
				pageSize,
				orderBy: "model_name",
				order: "asc",
			});

			setCount(counts);

			const transformedData = modelData.map((model: any) => ({
				id: model.ID,
				imageUrl: model.images
					? JSON.parse(model.images)?.filter((item: any) => item !== null)
					: [],
				title: model.model_name,
				dimensions: {
					height: model.demention_height,
					width: model.demention_width,
					depth: model.demention_depth,
				},
				rackSpaces: model.demention_rack_spaces,
				partNumber: model.part_number,
				msrp: model.MSRP,
				dealerPrice: model.dealer_price,
			}));

			setModels(transformedData);
			setIsLoading(false);
		} catch (error) {
			console.error("Failed to fetch admins", error);
		}
	};

	useEffect(() => {
		fetchModels();
	}, [page, pageSize]);

	useEffect(() => {
		setPage(1);
	}, [pageSize]);

	if (isLoading) return <Loader isLoading={isLoading} />;

	return (
		<Box sx={{ maxWidth: 1100, mx: "auto", px: 2, py: 3 }}>
			<Grid container spacing={3}>
				{models?.map((rack: any) => (
					<Grid item xs={12} sm={6} md={4} key={rack.id}>
						<Card
							sx={{
								height: "100%",
								display: "flex",
								flexDirection: "column",
								border: "1px solid #E2E8F0",
								borderRadius: 2,
								transition: "all 0.2s ease",
								"&:hover": {
									borderColor: "#00B4D8",
									boxShadow: "0 4px 12px rgba(0, 180, 216, 0.1)",
								},
							}}
						>
							<Box sx={{ p: 1.5 }}>
								<ImageViewer images={rack.imageUrl} height={180} />
							</Box>

							<CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
								<Typography variant="h6" gutterBottom>
									{rack.title}
								</Typography>

								<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									Part # {rack.partNumber}
								</Typography>

								<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
									{rack.dimensions.height}" H x {rack.dimensions.width}" W x {rack.dimensions.depth}" D
									&nbsp;&bull;&nbsp; {rack.rackSpaces} Rack Spaces
								</Typography>

								<Box sx={{ mt: 1.5 }}>
									<Typography variant="body2">
										MSRP: ${Number(rack.msrp).toFixed(2)}
									</Typography>
									<Typography variant="body2">
										{process.env.REACT_APP_DEALER_NAME} Price: ${Number(rack.dealerPrice).toFixed(2)}
									</Typography>
								</Box>

								<Button
									variant="contained"
									color="primary"
									fullWidth
									sx={{ mt: "auto", pt: 1, pb: 1 }}
									onClick={() => navigate(`/models/${rack.id}/view`)}
								>
									Select Model
								</Button>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>

			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
				<BasicPagination
					pageCount={Math.ceil(count / pageSize)}
					page={page}
					setPage={setPage}
					pageNumber={pageSize}
					setPageNumber={setPageSize}
				/>
			</Box>
		</Box>
	);
};
